const DEFAULT_API_BASE = 'https://genia.daraforce.com/api/v1';
const MESSAGE_MAX = 4000;

export function defaultApiBaseUrl(): string {
  return DEFAULT_API_BASE;
}

export function normalizeApiBaseUrl(url: string | undefined): string {
  const raw = (url ?? DEFAULT_API_BASE).trim().replace(/\/+$/, '');
  return raw || DEFAULT_API_BASE;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function validateOutgoingMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) {
    throw new Error('EMPTY_MESSAGE');
  }
  if (trimmed.length > MESSAGE_MAX) {
    throw new Error('MESSAGE_TOO_LONG');
  }
  return trimmed;
}

export function friendlyError(code: string): string {
  switch (code) {
    case 'ORIGIN_NOT_ALLOWED':
    case 'ORIGIN_MISSING':
      return 'Este asistente no está habilitado para este sitio.';
    case 'WIDGET_INSTALLATION_REVOKED':
    case 'WIDGET_INSTALLATION_INVALID':
      return 'Este asistente no está disponible.';
    case 'RATE_LIMIT_EXCEEDED':
    case 'QUOTA_EXCEEDED':
      return 'Se alcanzó el límite temporal. Intentá nuevamente en unos minutos.';
    case 'WIDGET_SESSION_EXPIRED':
    case 'WIDGET_SESSION_INVALID':
    case 'WIDGET_SESSION_REVOKED':
      return 'La sesión expiró. Reintentá el mensaje.';
    case 'ENGINE_ERROR':
      return 'No pude procesar la consulta en este momento.';
    case 'NETWORK_ERROR':
      return 'No pudimos conectar con el asistente. Intentá más tarde.';
    case 'EMPTY_MESSAGE':
      return 'Escribí un mensaje para continuar.';
    case 'MESSAGE_TOO_LONG':
      return 'El mensaje es demasiado largo.';
    default:
      return 'Ocurrió un problema. Intentá nuevamente.';
  }
}

export type ScriptDataAttrs = {
  widgetKey?: string;
  apiBaseUrl?: string;
  position?: 'left' | 'right';
  locale?: string;
  /** When false, skip data-attribute autoInit (use programmatic MIA.init). */
  autoInit?: boolean;
};

export function readScriptDataAttrs(doc: Document = document): ScriptDataAttrs {
  const scripts = Array.from(doc.getElementsByTagName('script'));
  const current =
    scripts.find((s) => s.src && /widget\.js(\?|$)/i.test(s.src)) ??
    scripts[scripts.length - 1];
  if (!current) return {};
  const position = current.getAttribute('data-position');
  const autoInitAttr = current.getAttribute('data-auto-init');
  return {
    widgetKey: current.getAttribute('data-widget-key') ?? undefined,
    apiBaseUrl: current.getAttribute('data-api-base') ?? undefined,
    position: position === 'left' || position === 'right' ? position : undefined,
    locale: current.getAttribute('data-locale') ?? undefined,
    autoInit: autoInitAttr === null ? true : autoInitAttr !== 'false',
  };
}
